import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { useDarkMode } from "../ThemeContext";

const ThemedReaderFrame = forwardRef(function ThemedReaderFrame(
  { documentClassName, onLoad, ...props },
  ref,
) {
  const [, , theme] = useDarkMode();
  const frameRef = useRef(null);
  useImperativeHandle(ref, () => frameRef.current);
  const applyTheme = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    const root = doc?.documentElement;
    if (!root) return;

    root.classList.add("embedded-reader");
    if (documentClassName) root.classList.add(documentClassName);
    root.dataset.theme = theme;
    // Same-origin frames do not inherit the site's palette.
    const palette = getComputedStyle(document.documentElement);
    for (const name of [
      "canvas",
      "surface",
      "manilla",
      "espresso",
      "muted",
      "border",
      "terracotta",
      "tulsi-olive",
      "bhakti-fig",
      "suhrad-sky",
    ]) {
      const property = `--cream-${name}`;
      root.style.setProperty(property, palette.getPropertyValue(property));
    }
    if (!doc.getElementById("site-reader-theme")) {
      const link = doc.createElement("link");
      link.id = "site-reader-theme";
      link.rel = "stylesheet";
      link.href = `${process.env.PUBLIC_URL}/styles/EmbeddedReaderTheme.css`;
      (doc.head || root).appendChild(link);
    }
  }, [theme, documentClassName]);

  useEffect(() => {
    applyTheme();
  }, [applyTheme]);

  return (
    <iframe
      {...props}
      ref={frameRef}
      onLoad={(event) => {
        applyTheme();
        onLoad?.(event);
      }}
    />
  );
});

export default ThemedReaderFrame;
